import React, { useCallback, useMemo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import LabelWithPars from '../../../components/LabelWithPars/LabelWithPars';
import ItemContent from '../../../components/ItemContent/ItemContent';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { NSPackages } from '../../../types/interfaces/NSPackages';
import useDescLangs from '../../../utils/useDescLangs';
import { EStatus } from '../../../types/enums/EStatus';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';
import { EPaths } from '../../../types/enums/EPaths';
import { SessionContext } from '../../../contexts/session/SessionContext';
import { CircularProgress, Typography } from '@material-ui/core';
import countriesJSON from '../../../assets/countries/countries.json';
import { EPackageCoverageGroup } from '../../../types/enums/EPackageCoverageGroup';
import moment from 'moment';

const coverageGroupLabels: Record<EPackageCoverageGroup, string> = {
  [EPackageCoverageGroup.CruiseAndLand]: 'Cruise+Land',
  [EPackageCoverageGroup.Cruise]: 'Cruise Only',
  [EPackageCoverageGroup.Other]: 'Other',
};

const EditPackage = () => {
  const { id } = useParams<{ id: string }>();
  const { languagesList, reduceLangsList } = useDescLangs();
  const { t } = useTranslation();

  const { permissions } = useContext(SessionContext);

  const descriptionMapper = useCallback(
    (data: NSPackages.IPackageDetails, key: string): IEditForm[][] => {
      return [
        [
          {
            type: 'input',
            fieldName: 'title',
            label: <LabelWithPars label={t(tPaths.editFormLabels.title)} info={key.toUpperCase()} />,
            initialValue: data.title[key],
          },
          {
            type: 'switch',
            fieldName: 'prefersTitleHidden',
            label: t(tPaths.editFormLabels.prefersTitleHidden),
            initialValue: data.prefersTitleHidden || false,
          },
          {
            type: 'description',
            fieldName: 'description',
            label: <LabelWithPars label={t(tPaths.editFormLabels.description)} info={key.toUpperCase()} />,
            initialValue: data.description[key],
            multiline: true,
          },
          {
            type: 'select',
            fieldName: 'promocodeGroups',
            fullWidth: false,
            // show the last from list, remove later
            initialValue: data.promocodeGroups,
            label: (
              <Typography style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 10 }}>
                {t(tPaths.editFormLabels.promocodeGroups)}
              </Typography>
            ),
            variants: data.allPromotionGroups.map((group) => ({
              value: group,
              label: group,
            })),
          },
          {
            type: 'select',
            fieldName: 'coverageGroup',
            fullWidth: false,
            initialValue: data.coverageGroup || EPackageCoverageGroup.Other,
            label: (
              <Typography style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 10 }}>
                {t(tPaths.editFormLabels.packageType)}
              </Typography>
            ),
            variants: (data.allCoverageGroups || Object.values(EPackageCoverageGroup)).map((group) => ({
              value: group,
              label: coverageGroupLabels[group as EPackageCoverageGroup] || group,
            })),
          },
        ],
        [
          {
            type: 'image',
            fieldName: 'imageIntro',
            label: t(tPaths.editFormLabels.packageIntro),
            initialValue: data.imageIntro[key],
          },
          {
            type: 'image',
            fieldName: 'imageConfirmation',
            label: t(tPaths.editFormLabels.packageConfirmation),
            initialValue: data.imageConfirmation[key],
          },
        ],
      ];
    },
    [t],
  );

  const settingsMapper = useCallback(
    (data: NSPackages.IPackageDetails): IEditForm[][] => {
      const durationAsMonths: number = moment.duration(data.runtime).asMonths();
      return [
        [
          {
            type: 'read',
            fieldName: 'carrierBundle',
            label: t(tPaths.editFormLabels.carrierBundle),
            initialValue: data.carrierIdentifier,
          },
          {
            type: 'read',
            fieldName: 'price',
            label: t(tPaths.editFormLabels.price),
            initialValue: data.price,
          },
          {
            type: 'read',
            fieldName: 'autoRenewable',
            label: t(tPaths.editFormLabels.autoRenewing),
            initialValue: t(tPaths.global.yesNo(data.autoRenewing)),
          },
          {
            type: 'read',
            fieldName: 'validity',
            label: t(tPaths.editFormLabels.validity),
            initialValue: durationAsMonths + ' ' + t(tPaths.editFormLabels.durationValidity(durationAsMonths)),
          },
          {
            type: 'read',
            fieldName: 'variation',
            label: t(tPaths.editFormLabels.variation),
            initialValue: data.variations.map(
              (variation, index) =>
                `${variation.dataAmount} ${variation.dataUnit}${data.variations.length !== index + 1 ? '/' : ''}`,
            ),
          },
        ],
      ];
    },
    [t],
  );

  const tabs = useMemo(
    () => [
      ...languagesList.map((lang) => ({ key: lang.key, label: lang.display })),
      { key: 'settings', label: t(tPaths.editFormTabs.settings) },
    ],
    [languagesList, t],
  );

  const tabsContentMapper = useCallback(
    (data: NSPackages.IPackageDetails) => ({
      ...languagesList.reduce(
        (acc, cv) => ({
          ...acc,
          [cv.key]: descriptionMapper(data, cv.key),
        }),
        {},
      ),
      settings: settingsMapper(data),
    }),
    [descriptionMapper, languagesList, settingsMapper],
  );

  const updateDataFormatter = useCallback(
    (fields: any) => {
      return {
        title: reduceLangsList(fields, 'title'),
        prefersTitleHidden: fields.en.prefersTitleHidden.value,
        description: reduceLangsList(fields, 'description'),
        imageIntro: reduceLangsList(fields, 'imageIntro'),
        imageConfirmation: reduceLangsList(fields, 'imageConfirmation'),
        status: fields.status ? EStatus.Active : EStatus.Inactive,
        promocodeGroups: reduceLangsList(fields, 'promocodeGroups'),
        coverageGroup: fields.en.coverageGroup.value,
        visibleInMainList: fields.visibleInMainList,
      };
    },
    [reduceLangsList],
  );

  const switches = useMemo(
    () => [
      { key: 'visibleInMainList', label: t(tPaths.editFormLabels.visibleInMainList) },
      { key: 'status', label: t(tPaths.itemContent.active) },
    ],
    [t],
  );
  const switchesDataMapper = useCallback((data: NSPackages.IPackageDetails) => {
    return {
      status: data.status.current === EStatus.Active,
      visibleInMainList: data.visibleInMainList ?? true,
    };
  }, []);

  // example of field validation
  // const handleInputValidation = useCallback(
  //   (fields) => {
  //     const hasError = languagesList.reduce(
  //       (acc, cv) => !!!fields[cv.key].description?.length,
  //       false,
  //     );
  //     if (hasError) {
  //       return {
  //         tabsErrors: languagesList.map((lang) => ({
  //           tab: lang.key,
  //           fieldsErrors: [{ field: 'description', errorText: 'Field must not be empty' }],
  //         })),
  //       };
  //     }
  //     return;
  //   },
  //   [languagesList],
  // );

  return (
    <>
      {languagesList.length ? (
        <ItemContent
          itemId={id}
          title={t(tPaths.itemContent.editPackage)}
          path={EPaths.Packages}
          tabs={tabs}
          tabsContentMapper={tabsContentMapper}
          updateDataFormatter={updateDataFormatter}
          switches={switches}
          switchesDataMapper={switchesDataMapper}
          editable={permissions.packages.edit}
        />
      ) : (
        <CircularProgress />
      )}
    </>
  );
};

export default EditPackage;
